import { useState, useEffect } from "react";
import { Table, CombinedTable } from "@/types/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { checkTableReservationImpact, checkTableInCombinations } from "@/lib/reservationProtectionUtils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface DeleteTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: Table | null;
  combinedTables: CombinedTable[];
  onConfirm: () => void;
  onDeactivateInstead?: (tableId: string) => void;
}

export function DeleteTableDialog({
  open,
  onOpenChange,
  table,
  combinedTables,
  onConfirm,
  onDeactivateInstead,
}: DeleteTableDialogProps) {
  const [checking, setChecking] = useState(true);
  const [futureReservations, setFutureReservations] = useState<any[]>([]);
  const [futureCount, setFutureCount] = useState(0);
  const [inCombinations, setInCombinations] = useState<any[]>([]);

  useEffect(() => {
    if (open && table) {
      checkReservations();
    }
  }, [open, table]);

  const checkReservations = async () => {
    if (!table) return;
    
    setChecking(true);
    try {
      // Vérifier les réservations futures
      const impact = await checkTableReservationImpact(table.id);
      setFutureReservations(impact.futureReservations);
      setFutureCount(impact.futureCount);

      // Vérifier si la table fait partie de combinaisons actives
      const combinations = await checkTableInCombinations(table.id);
      setInCombinations(combinations.combinations);
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);
    } finally {
      setChecking(false);
    }
  };

  const canDelete = futureCount === 0 && inCombinations.length === 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              Vous êtes sur le point de supprimer définitivement la table{" "}
              <strong>{table?.name}</strong>.
            </p>

            {checking ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full"></div>
                <span>Vérification des réservations...</span>
              </div>
            ) : (
              <>
                {/* Bloqué si dans des combinaisons */}
                {inCombinations.length > 0 && (
                  <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
                    <p className="font-medium text-destructive mb-2">
                      ❌ Cette table fait partie de {inCombinations.length} combinaison(s) active(s) :
                    </p>
                    <ul className="space-y-1 text-sm">
                      {inCombinations.map(combo => (
                        <li key={combo.id}>• {combo.name}</li>
                      ))}
                    </ul>
                    <p className="text-sm mt-2">
                      Vous devez d'abord désactiver ou supprimer ces combinaisons.
                    </p>
                  </div>
                )}

                {/* Bloqué si réservations futures */}
                {futureCount > 0 && (
                  <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
                    <p className="font-medium text-destructive mb-2">
                      ❌ {futureCount} réservation(s) future(s) sont assignées à cette table :
                    </p>
                    <ul className="space-y-1 text-sm">
                      {futureReservations.slice(0, 3).map(res => (
                        <li key={res.id}>
                          • {format(new Date(res.date), "d MMM yyyy", { locale: fr })} à{" "}
                          {res.startTime.slice(0, 5)} - {res.customerName}
                        </li>
                      ))}
                      {futureCount > 3 && (
                        <li className="text-muted-foreground italic">
                          ... et {futureCount - 3} autre(s)
                        </li>
                      )}
                    </ul>
                    <p className="text-sm mt-2">
                      💡 Vous pouvez désactiver la table au lieu de la supprimer.
                    </p>
                  </div>
                )}

                {/* Aucun problème */}
                {canDelete && (
                  <div className="bg-green-500/10 p-3 rounded-md border border-green-500/20">
                    <p className="text-green-700 dark:text-green-400">
                      ✅ Aucune réservation future affectée. Vous pouvez supprimer en toute sécurité.
                    </p>
                  </div>
                )}

                <p className="text-destructive font-medium text-sm">
                  ⚠️ Cette action est irréversible.
                </p>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          {!canDelete && futureCount > 0 && onDeactivateInstead && (
            <Button
              variant="outline"
              onClick={() => {
                onDeactivateInstead(table!.id);
                onOpenChange(false);
              }}
            >
              Désactiver à la place
            </Button>
          )}
          <AlertDialogAction
            onClick={onConfirm}
            disabled={!canDelete || checking}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
